import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { OnFarmImplantShipmentSourceType } from '@/services/on-farm-implant';

import type { ShipmentInSelection } from '../../utils/shipmentSelection';

type ShipmentsInSelectionProps = {
  sourceType: OnFarmImplantShipmentSourceType;
  shipments: ShipmentInSelection[];
  readOnly?: boolean;
  onRemove: (shipment: ShipmentInSelection) => void;
};

export const ShipmentsInSelection = ({
  sourceType,
  shipments,
  readOnly = false,
  onRemove,
}: ShipmentsInSelectionProps) => {
  const { t } = useTranslation();
  if (shipments.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="xs"
            data-testid="shipments-in-selection"
          />
        }
      >
        <i className="pi pi-box text-xs" />
        {shipments.length === 1
          ? shipments[0].shipmentNumber
          : t('onFarmImplant.sourceCard.shipments.count', {
              count: shipments.length,
            })}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <PopoverHeader>
          <PopoverTitle>
            {t('onFarmImplant.sourceCard.shipments.inSelectionTitle')}
          </PopoverTitle>
          {!readOnly && (
            <PopoverDescription className="text-xs">
              {t(
                sourceType === 'fresh'
                  ? 'onFarmImplant.sourceCard.shipments.removeHintFresh'
                  : 'onFarmImplant.sourceCard.shipments.removeHintFrozen',
              )}
            </PopoverDescription>
          )}
        </PopoverHeader>
        <ul className="flex max-h-64 flex-col overflow-auto">
          {shipments.map((shipment) => (
            <li
              key={shipment.shipmentId}
              className="flex items-center justify-between gap-2 border-t border-gray-100 py-1.5 first:border-t-0"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-medium">
                  {shipment.shipmentNumber}
                </span>
                <span className="text-xs text-gray-500">
                  {sourceType === 'fresh'
                    ? t('onFarmImplant.sourceCard.shipments.freshCoverage', {
                        selected: shipment.selectedCount,
                        total: shipment.recordKeys.length,
                      })
                    : t('onFarmImplant.sourceCard.shipments.frozenCoverage', {
                        embryos: shipment.selectedCount,
                        records: shipment.totalRecords,
                      })}
                </span>
              </div>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onRemove(shipment)}
                  aria-label={t('onFarmImplant.sourceCard.shipments.remove', {
                    shipment: shipment.shipmentNumber,
                  })}
                >
                  {t('onFarmImplant.sourceCard.shipments.removeShort')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
